<template>
  <div
    v-if="countdownBanner && countdownBanner.headline"
    class="bg-br-green-light py-4 relative text-blackly-dark"
    data-testid="global-countdown-banner"
  >
    <div
      class="container flex lg:flex-nowrap items-center lg:justify-between gap-x-4 gap-y-2"
      :class="{
        'flex-wrap': !isTickerMode,
        'flex-nowrap': isTickerMode,
      }"
    >
      <div
        class="w-full lg:text-left"
        :class="{
          'md:flex-1': !isTickerMode && showCountdownBanner,
          'text-center': !isTickerMode,
          'w-full sm:w-auto sm:absolute sm:inset-x-0 sm:whitespace-nowrap sm:overflow-hidden sm:flex-1 text-left sm:text-center':
            !showCountdownBanner && isTickerMode,
        }"
      >
        <div :class="{ 'sm:ticker-wrapper': !showCountdownBanner && isTickerMode }">
          <div class="inline-flex items-center gap-1">
            <span
              v-for="i in repetitionCount"
              :key="i"
              :class="{ 'hidden sm:inline': i > 1 }"
              class="strong-bold uppercase text-2xl lg:text-4xl"
              v-html="countdownBanner.headline"
            ></span>
          </div>
        </div>
      </div>

      <div
        :class="{ 'flex-wrap md:flex-nowrap gap-x-4': showCountdownBanner, 'flex-1 sm:flex-auto': isTickerMode }"
        class="relative z-1 flex items-center md:flex-1 w-full justify-center lg:justify-around sm:gap-x-6 gap-y-2"
      >
        <div v-if="showCountdownBanner" class="items-center flex space-x-2 text-2xl lg:text-4xl">
          <span class="w-9 lg:w-header-bar-height-mobile text-center"
            >{{ daysLeft }}<small class="ml-px uppercase text-base text-blackly-dark opacity-50">d</small></span
          >
          <span class="">:</span>
          <span class="w-9 lg:w-header-bar-height-mobile text-center"
            >{{ hoursLeft }}<small class="ml-px uppercase text-base text-blackly-dark opacity-50">h</small></span
          >
          <span class="">:</span>
          <span class="w-9 lg:w-header-bar-height-mobile text-center"
            >{{ minutesLeft }}<small class="ml-px uppercase text-base text-blackly-dark opacity-50">m</small></span
          >
          <span class="">:</span>
          <span class="w-9 lg:w-header-bar-height-mobile text-center"
            >{{ secondsLeft }}<small class="ml-px uppercase text-base text-blackly-dark opacity-50">s</small></span
          >
        </div>

        <div class="sm:w-full" v-if="!showCountdownBanner && isTickerMode"></div>

        <div v-if="countdownBanner.cta && countdownBanner.cta.length" class="flex items-center justify-end">
          <LinkField class="btn-pill-white border-0 whitespace-nowrap" v-bind="countdownBanner.cta[0]" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useGlobalBannerStore } from '@/stores/global-banner';
import { useCountdown } from '@/composables/useCountdown';
import LinkField from '@/components/common/LinkField.vue';

const globalBannerStore = useGlobalBannerStore();

const countdownBanner = globalBannerStore.countdownBanner;

const { isActive, daysLeft, hoursLeft, minutesLeft, secondsLeft } = useCountdown(
  new Date(countdownBanner.countdownUntil)
);

const showCountdownBanner = computed(() => {
  return countdownBanner && countdownBanner.countdownUntil && isActive.value;
});

const isTickerMode = computed(() => {
  return countdownBanner && countdownBanner.headline && countdownBanner.isTickerMode && !showCountdownBanner.value;
});

// Minimum character length for the ticker
const MIN_TICKER_LENGTH = 260;

// Calculate how many times to repeat the headline
const repetitionCount = computed(() => {
  if (!countdownBanner || !countdownBanner.headline) return 1;
  if (showCountdownBanner.value || isTickerMode.value === false) return 1;

  // Strip HTML tags to get plain text length
  const plainText = countdownBanner?.headline?.replace(/<[^>]*>/g, '');
  const textLength = plainText.length;

  if (textLength === 0) return 2; // Fallback

  // Calculate repetitions needed to reach minimum length
  const needed = Math.ceil(MIN_TICKER_LENGTH / textLength);

  // Ensure even number for seamless -50% animation (at least 2)
  return needed % 2 === 0 ? Math.max(needed, 2) : Math.max(needed + 1, 2);
});
</script>
